import { Box, Flex, Text, useDisclosure } from "@chakra-ui/react";
import { FB, LINKEDIN, MessageIcon, ShareIcon } from "@root/assets/icons";
import {
  CHAT_MODE,
  CHATBOT_TYPE,
  getSearchTabs,
  LEGAL_CHATMODE,
  modelOptions,
  modelOptionsFile,
  modelOptionsWebSearch,
  ROUTE_NAMES,
} from "@root/constants";
import useMediaQ from "@root/hooks/useMediaQ";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { StyledHeaderContainer } from "./style";
import { getThreadList } from "@root/store/selectors/entities/thread";
import { useMainChatContext } from "@root/context/kps-chat-context";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FileIcon } from "@root/assets/icons/file-icon";
import { useProjectsContext } from "@root/context/projects-context";
import { ProjectSelect } from "@root/pages/dashboard/components/ProjectSelect";
import { ProjectsService } from "@root/services/projects";
import { ChatHistoryDrawer } from "../chat-history/chat-history-drawer";
import {
  getChatType,
  handleStartChat as handleStartChatUtil,
} from "@root/utils";
import RefreshIcon from "@root/assets/icons/history-refresh-icon";
import { DownloadIcon } from "@root/assets/icons/downloadIcon";
import PlusIcon from "@root/assets/icons/plusicon";
import { CommonButton } from "../common/button/commonButton";
import PillToggle from "./UploadDashboad.tsx/permit-toggler";
import ModelSelector from "@root/pages/dashboard/components/model-selection-toggler.tsx/model-selection";
import ModelDisplay from "@root/pages/dashboard/components/model-selection-toggler.tsx/model-text";
import { AttachedFilesList } from "./AttachedFilesList";

export const DashboardHeader = ({ data, hideBtn, copyRef, payload }: any) => {
  const [_, systemChat]: any = useSelector(getThreadList);
  let threadKuid = "";
  if (systemChat) {
    threadKuid = systemChat?.threadKuid;
  }

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { t } = useTranslation("common");
  const {
    updateChatModeValue: setChatMode,
    legalChatMode,
    docChatMode,
    toggleRightSidebar,
    selectedProject,
    setSelectedProject,
    currentPathname,
    onProjectChange,
    downloadPdfOfThread,
    downloadPdfLoading,
    currentMessageResponse,
    model,
    setModel,
    openProjectsListing,
    sourcesData,
    setLegalMode,
    attachedFile,
    setAttachedFile,
    isPolling,
    currentThread,
  } = useMainChatContext();
  const { projectsData, projectsOptions, handleProjectClick, fetchDocuments } =
    useProjectsContext();

  const firstRec = currentThread?.[0]?.meta?.c;

  const { pathname } = useLocation();
  const isProjectChatbot = currentPathname?.includes(ROUTE_NAMES.DOCUMENT_CHAT);
  const isDrawingAnalysis = pathname.includes(
    ROUTE_NAMES.DRAWING_ANALYSIS_CHAT
  );
  const { id } = useParams();
  const isKCSChat = pathname.includes(ROUTE_NAMES.KCS);
  const projectService = new ProjectsService();
  const navigate = useNavigate();
  const project = projectsData?.rows?.find(
    (project: any) => project?.id === selectedProject
  );
  const isLawInfoCentre = legalChatMode === LEGAL_CHATMODE.LAW_INFO_CENTER;
  const isLegalChatbot = pathname.startsWith("/chat");

  const chatType = useMemo(() => {
    return getChatType(pathname);
  }, [pathname]);

  const getProjectId = () => {
    if (isProjectChatbot) {
      if (!selectedProject) {
        return Number(CHATBOT_TYPE.MULTI_DOCUMENT_CHATBOT);
      } else if (selectedProject) {
        return selectedProject;
      }
    }
    return undefined;
  };

  const handleStartChat = () => {
    handleStartChatUtil(pathname, navigate);
  };

  useEffect(() => {
    if (selectedProject) {
      fetchDocuments({
        projectId: project?.id,
      });
    }
  }, [selectedProject]);

  useEffect(() => {
    if (
      isProjectChatbot &&
      docChatMode?.value === CHAT_MODE.PROJECT &&
      !selectedProject &&
      projectsOptions?.length
    ) {
      setSelectedProject(projectsOptions[0]?.value);
    }
  }, [
    docChatMode?.value,
    isProjectChatbot,
    projectsOptions,
    selectedProject,
    setSelectedProject,
  ]);

  const renderToggleButton = () => {
    const isPanelVisible = openProjectsListing || sourcesData;

    if (pathname.startsWith(ROUTE_NAMES.DOCUMENT_CHAT)) {
      return (
        <Flex
          // align="center"
          gap={2}
          flexDirection={{
            base: "column",
            lg: isPanelVisible ? "column" : "row",
          }}
          minW="0"
        >
          {isProjectChatbot && (
            <Flex alignItems="center" gap="20px">
              <ProjectSelect
                selectedValue={selectedProject}
                setSelectedValue={setSelectedProject}
                options={projectsOptions}
                onCustomChange={() => onProjectChange(false)}
                variant="secondary"
                disabled={isPolling}
                tooltipLabel={
                  isPolling
                    ? t("translate.searchModeDisabledTooltipStreaming")
                    : undefined
                }
              />
            </Flex>
          )}
          <Box alignSelf={"flex-start"}>
            <Flex gap={"20px"}>
              <AttachedFilesList
                files={attachedFile}
                onDelete={(fileId: string) => {
                  setAttachedFile((prev: any) =>
                    prev.filter((doc: any) => doc.id !== fileId)
                  );
                }}
              />
              {attachedFile?.length === 0 && (
                <Box>
                  <ModelDisplay modelKey={chatType} />
                </Box>
              )}
            </Flex>
          </Box>
        </Flex>
      );
    }

    if (isLegalChatbot) {
      return (
        <Flex gap={"30px"} alignItems={"center"}>
          <Box>
            <PillToggle
              options={getSearchTabs()}
              active={legalChatMode}
              setActive={setLegalMode}
              setState={setLegalMode}
              disabled={attachedFile.length > 0 || isPolling}
              tooltipLabel={
                attachedFile.length > 0 || isPolling
                  ? isPolling
                    ? t("translate.searchModeDisabledTooltipStreaming")
                    : t("translate.searchModeDisabledTooltip")
                  : undefined
              }
              variant="secondary"
              onCustomChange={(newState: number) => {
                onProjectChange(true);
              }}
            />
          </Box>
          <Box>
            <ModelSelector
              options={
                attachedFile.length > 0
                  ? modelOptionsFile
                  : isLawInfoCentre
                  ? modelOptions
                  : modelOptionsWebSearch
              }
              value={model} // ← this is the current selected model
              onChange={(value: number) => {
                setModel(value);
              }}
              onCustomChange={(value: number) => {
                onProjectChange(true);
              }}
              disabled={isPolling}
              tooltipLabel={
                isPolling
                  ? t("translate.searchModeDisabledTooltipStreaming")
                  : undefined
              }
            />
          </Box>
        </Flex>
      );
    }
    return null;
  };

  const renderHeaderLeft = () => {
    const isPanelVisible = openProjectsListing || sourcesData;

    if (isDrawingAnalysis) {
      return (
        <Flex
          gap={2}
          alignItems={"center"}
          flexDirection={{
            base: "column",
            md: isPanelVisible ? "column" : "row",
          }}
          minW="0"
        >
          <ModelSelector
            options={modelOptions}
            value={model} // ← this is the current selected model
            onChange={setModel} // ← updates the selected model
          />

          <Text
            color={"#231F20"}
            fontSize="20px"
            fontWeight={600}
            noOfLines={1}
          >
            {t("translate.chatbot")}
          </Text>
        </Flex>
      );
    }
    if (isKCSChat) {
      return (
        <Flex
          gap={2}
          alignItems={"center"}
          flexDirection={{
            base: "column",
            md: isPanelVisible ? "column" : "row",
          }}
          minW="0"
        >
          <Text
            color={"#231F20"}
            fontSize="20px"
            fontWeight={600}
            noOfLines={1}
          >
            {firstRec?.thread?.chat_title ??
              currentMessageResponse?.thread?.chat_title ??
              t("translate.processing")}
          </Text>
        </Flex>
      );
    }

    return renderToggleButton();
  };

  const renderTopBar = () => {
    const isPanelVisible = openProjectsListing || sourcesData;

    return (
      <StyledHeaderContainer
        flexWrap="nowrap"
        gap="12px"
        px="15px"
        justifyContent="space-between"
      >
        <Box minW="0" flex="1 1 auto" maxW="100%">
          {renderHeaderLeft()}
        </Box>
        <Flex
          gap="10px"
          flexWrap={{ base: "wrap", lg: isPanelVisible ? "wrap" : "nowrap" }}
          alignItems="center"
          justifyContent="flex-end"
          minW="0"
          flexShrink={1}
        >
          {id && (
            <Box
              flexBasis={{
                base: "calc(50% - 5px)",
                md: isPanelVisible ? "calc(30% - 5px)" : "auto",
              }}
              minW={{
                base: "calc(50% - 5px)",
                md: isPanelVisible ? "calc(30% - 5px)" : "auto",
              }}
            >
              <CommonButton
                variant="primary"
                startIcon={
                  <PlusIcon width="18px" height="18px" color="white" />
                }
                onClick={handleStartChat}
                fontSize="14px"
                height="44px"
                px={6}
                width={{ base: "100%", md: isPanelVisible ? "100%" : "auto" }}
              >
                {t("translate.startChat")}
              </CommonButton>
            </Box>
          )}
          {isProjectChatbot && (
            <Box
              flexBasis={{
                base: "calc(50% - 5px)",
                md: isPanelVisible ? "calc(30% - 5px)" : "auto",
              }}
              minW={{
                base: "calc(50% - 5px)",
                md: isPanelVisible ? "calc(30% - 5px)" : "auto",
              }}
            >
              <CommonButton
                variant="secondary"
                startIcon={
                  <FileIcon width="18px" height="18px" color="#231F20" />
                }
                onClick={() => {
                  if (selectedProject) {
                    handleProjectClick(project);
                  }
                  toggleRightSidebar();
                }}
                fontSize="14px"
                height="44px"
                px={6}
                width={{ base: "100%", md: isPanelVisible ? "100%" : "auto" }}
              >
                {t("translate.totalDocs")}
              </CommonButton>
            </Box>
          )}
          {
            <Box
              flexBasis={{
                base: "calc(50% - 5px)",
                md: isPanelVisible ? "calc(30% - 5px)" : "auto",
              }}
              minW={{
                base: "calc(50% - 5px)",
                md: isPanelVisible ? "calc(30% - 5px)" : "auto",
              }}
            >
              <CommonButton
                variant="secondary"
                startIcon={<RefreshIcon />}
                onClick={onOpen}
                fontSize="14px"
                height="44px"
                px={6}
                width={{ base: "100%", md: isPanelVisible ? "100%" : "auto" }}
              >
                {t("translate.viewHistory")}
              </CommonButton>
            </Box>
          }
          {id && (
            <Box
              flexBasis={{
                base: "calc(50% - 5px)",
                md: isPanelVisible ? "calc(30% - 5px)" : "auto",
              }}
              minW={{
                base: "calc(50% - 5px)",
                md: isPanelVisible ? "calc(30% - 5px)" : "auto",
              }}
            >
              <CommonButton
                variant="secondary"
                startIcon={<DownloadIcon />}
                onClick={() => downloadPdfOfThread({ threadId: id })}
                isLoading={downloadPdfLoading}
                fontSize="14px"
                height="44px"
                px={6}
                width={{ base: "100%", md: isPanelVisible ? "100%" : "auto" }}
              >
                {t("translate.downloadAnswer")}
              </CommonButton>
            </Box>
          )}
          <ChatHistoryDrawer
            isOpen={isOpen}
            onClose={onClose}
            chatbotId={chatType} // pass the chatbotId dynamically
            mode={legalChatMode}
            projectId={getProjectId()}
          />
        </Flex>
      </StyledHeaderContainer>
    );
  };

  return <Box>{renderTopBar()}</Box>;
};
